import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronLeft, Edit, Printer, Receipt, CalendarDays,
  Phone, MapPin, Users, Award, FileText, UserCheck,
  CreditCard, BookOpen, CheckCircle, Clock, AlertTriangle,
  Loader2, Plus, History, ChevronDown, ChevronUp, Wallet, Trash2, X, MoreVertical,
  PhoneCall
} from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDate, formatCurrency } from '../../lib/utils';
import { api, API_URL } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import AttendanceCalendar from '../../components/AttendanceCalendar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import SharedFeesTab from '../../components/SharedFeesTab';

interface StudentDetail {
  id: number;
  student_name: string;
  gender: string;
  date_of_birth: string;
  class_name: string;
  section_name: string;
  batch_name: string;
  roll_no: string;
  registration_no: string;
  admission_date: string;
  status: string;
  school_name: string;
  school_board: string;
  previous_percentage: number;
  scholarship_applicable: number;
  parents?: {
    father_name: string;
    father_phone_1: string;
    father_phone_2: string;
    mother_name: string;
    mother_phone_1: string;
    mother_phone_2: string;
    whatsapp_number: string;
  };
  address?: {
    village_town: string;
    district: string;
    full_address: string;
  };
  documents?: Array<{ id: number; document_type: string; file_name: string; public_url?: string }>;
}

const statusColors: Record<string, string> = {
  active: 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300',
  drop: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  left: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  enquiry: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
};

const tabs = [
  { id: 'overview', label: 'Overview', icon: UserCheck },
  { id: 'parents', label: 'Parents', icon: Users },
  { id: 'fees', label: 'Fees', icon: CreditCard },
  { id: 'attendance', label: 'Attendance', icon: CalendarDays },
  { id: 'documents', label: 'Documents', icon: FileText },
];

const feeStatusConfig: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  paid: { label: 'Paid', color: 'text-lime-700 dark:text-lime-300', bg: 'bg-lime-100 dark:bg-lime-500/20', icon: CheckCircle },
  partial: { label: 'Partial', color: 'text-gold-700 dark:text-gold-300', bg: 'bg-gold-100 dark:bg-gold-500/20', icon: Clock },
  due: { label: 'Due', color: 'text-tangerine-700 dark:text-tangerine-300', bg: 'bg-tangerine-100 dark:bg-tangerine-500/20', icon: AlertTriangle },
  overdue: { label: 'Overdue', color: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: AlertTriangle },
};

export default function StudentProfilePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const queryClient = useQueryClient();

  // If coming from module flow, back goes to the module student list
  const module = searchParams.get('module');
  const classId = searchParams.get('classId');
  const batchId = searchParams.get('batchId');
  const backPath = module && classId
    ? `/modules/${module}/classes/${classId}/batches/${batchId || ''}`
    : '/students';

  const { data, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get<any>(`/api/students/${id}`);
      return response;
    },
    enabled: !!id,
    staleTime: 0,
  });

  const { data: feesData } = useQuery({
    queryKey: ['student-fees', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get<any>(`/api/fees/invoices?studentId=${id}`);
      return response;
    },
    enabled: !!id,
    staleTime: 0,
  });

  const student: StudentDetail = data?.data;

  const deleteStudent = useMutation({
    mutationFn: async () => api.delete(`/api/students/${id}`),
    onSuccess: () => {
      toast.success('Student deleted');
      queryClient.invalidateQueries({ queryKey: ['students'] });
      navigate(backPath);
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete student'),
  });
  const invoices = (feesData?.data?.items || feesData?.data || []) as any[];

  // Fees tab uses SharedFeesTab (no extra state needed)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-16">
        <p className="text-light-text-muted">Student not found</p>
        <Button asChild className="mt-4">
          <Link to="/students">Back to Students</Link>
        </Button>
      </div>
    );
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const photoDoc = student.documents?.find((d: any) => d.document_type === 'student_photo' && d.public_url);
  const photoUrl = photoDoc?.public_url
    ? (photoDoc.public_url.startsWith('http') ? photoDoc.public_url : `${API_URL}${photoDoc.public_url}`)
    : null;

  return (
    <>
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => navigate(backPath)}>
              <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text">Student Profile</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-10 gap-2" onClick={() => setShowCallModal(true)}>
            <PhoneCall className="w-4 h-4" />
            <span className="hidden sm:inline">Call</span>
          </Button>
          <Button variant="outline" size="sm" className="h-10 gap-2" asChild>
            <Link to={`/students/${id}/edit${module && classId ? `?module=${module}&classId=${classId}&batchId=${batchId || ''}` : ''}`}>
              <Edit className="w-4 h-4" />
              <span className="hidden sm:inline">Edit</span>
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="h-10 gap-2" asChild>
            <Link to={`/print/student/${id}`}>
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="h-10 gap-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </div>

      {/* Profile Card */}
      <div className="glass-card p-4 sm:p-6 rounded-2xl">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-center sm:items-start">
          {/* Left: Photo + Status */}
          <div className="flex flex-col items-center shrink-0">
            <div className="relative">
              <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden border-4 border-purple-200 dark:border-purple-500/30 shadow-lg cursor-pointer group" onClick={() => photoUrl && setShowPhotoModal(true)}>
                {photoUrl ? (
                  <>
                    <img src={photoUrl} alt={student.student_name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center rounded-2xl">
                      <span className="text-white text-xs opacity-0 group-hover:opacity-100 bg-black/40 px-2 py-1 rounded-full transition-opacity">View</span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full gradient-purple flex items-center justify-center text-white text-4xl sm:text-5xl md:text-6xl font-bold">
                    {getInitials(student.student_name)}
                  </div>
                )}
              </div>
            </div>
            <span className={`mt-2.5 px-3 py-0.5 rounded-full text-[11px] font-medium tracking-wide ${statusColors[student.status] || statusColors.active}`}>
              {student.status?.charAt(0).toUpperCase() + student.status?.slice(1)}
            </span>
          </div>

          {/* Right: Name + Stats */}
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <h2 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text">{student.student_name}</h2>
            <p className="text-sm text-light-text-muted mt-0.5">{student.registration_no}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
              <div className="p-2.5 sm:p-3 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 text-center">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mx-auto mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-purple-900 dark:text-purple-100">{student.class_name}</p>
                <p className="text-[10px] sm:text-[11px] text-purple-600/70 dark:text-purple-300/60 uppercase tracking-wide mt-0.5">Class</p>
              </div>
              <div className="p-2.5 sm:p-3 rounded-xl bg-lime-50 dark:bg-lime-500/10 border border-lime-100 dark:border-lime-500/20 text-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-lime-600 mx-auto mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-lime-900 dark:text-lime-100">{student.section_name || student.batch_name || 'N/A'}</p>
                <p className="text-[10px] sm:text-[11px] text-lime-600/70 dark:text-lime-300/60 uppercase tracking-wide mt-0.5">Batch</p>
              </div>
              <div className="p-2.5 sm:p-3 rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-100 dark:border-sky-500/20 text-center">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-sky-600 mx-auto mb-1" />
                <p className="text-xs sm:text-sm font-semibold text-sky-900 dark:text-sky-100">{formatDate(student.admission_date)}</p>
                <p className="text-[10px] sm:text-[11px] text-sky-600/70 dark:text-sky-300/60 uppercase tracking-wide mt-0.5">Admission</p>
              </div>
              <div className="p-2.5 sm:p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 text-center">
                <Award className={`w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 ${student.scholarship_applicable ? 'text-amber-500' : 'text-light-text-muted/40'}`} />
                <p className="text-xs sm:text-sm font-semibold text-amber-900 dark:text-amber-100">{student.scholarship_applicable ? 'Yes' : 'No'}</p>
                <p className="text-[10px] sm:text-[11px] text-amber-600/70 dark:text-amber-300/60 uppercase tracking-wide mt-0.5">Scholarship</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="flex border-b border-light-border dark:border-dark-border overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${
                activeTab === tab.id
                  ? 'text-purple-600 dark:text-purple-400 border-purple-500'
                  : 'text-light-text-muted border-transparent hover:text-light-text dark:hover:text-dark-text'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">Personal Information</h3>
                  <div className="divide-y divide-light-border dark:divide-dark-border rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">Gender</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text capitalize">{student.gender || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">Date of Birth</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">{student.date_of_birth ? formatDate(student.date_of_birth) : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">Roll Number</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">{student.roll_no || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">Batch</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">{student.batch_name || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">Academic Information</h3>
                  <div className="divide-y divide-light-border dark:divide-dark-border rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">School</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">{student.school_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">Board</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">{student.school_board || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between p-3">
                      <span className="text-xs sm:text-sm text-light-text-muted">Previous %</span>
                      <span className="text-xs sm:text-sm font-medium text-light-text dark:text-dark-text">{student.previous_percentage || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'parents' && (
            <div className="space-y-4 sm:space-y-6">
              {student.parents ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                  <div className="p-3 sm:p-4 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20">
                    <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-3 text-sm sm:text-base">Father</h4>
                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>Name:</strong> {student.parents.father_name || 'N/A'}</p>
                      <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>Phone:</strong> {student.parents.father_phone_1 || 'N/A'}</p>
                      {student.parents.father_phone_2 && (
                        <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>Alt:</strong> {student.parents.father_phone_2}</p>
                      )}
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 rounded-xl bg-magenta-50 dark:bg-magenta-500/10 border border-magenta-200 dark:border-magenta-500/20">
                    <h4 className="font-semibold text-magenta-700 dark:text-magenta-300 mb-3 text-sm sm:text-base">Mother</h4>
                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>Name:</strong> {student.parents.mother_name || 'N/A'}</p>
                      <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>Phone:</strong> {student.parents.mother_phone_1 || 'N/A'}</p>
                      {student.parents.mother_phone_2 && (
                        <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>Alt:</strong> {student.parents.mother_phone_2}</p>
                      )}
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 rounded-xl bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 sm:col-span-2">
                    <h4 className="font-semibold text-cyan-700 dark:text-cyan-300 mb-3 text-sm sm:text-base">Contact</h4>
                    <div className="space-y-2">
                      <p className="text-xs sm:text-sm text-light-text dark:text-dark-text"><strong>WhatsApp:</strong> {student.parents.whatsapp_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-light-text-muted py-8">No parent information available</p>
              )}
            </div>
          )}

          {activeTab === 'fees' && (
            <SharedFeesTab studentId={id!} invoices={invoices} queryKey={['student-fees', id!]} />
          )}

          {activeTab === 'attendance' && (
            <StudentAttendanceTab studentId={id!} />
          )}

          {activeTab === 'documents' && (
            <StudentDocumentsTab studentId={id!} documents={student.documents || []} />
          )}
        </div>
      </div>
    </div>

    {/* Photo modal */}
    {showPhotoModal && photoUrl && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        onClick={() => setShowPhotoModal(false)}
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          className="relative max-w-[90vw] max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <img src={photoUrl} alt={student?.student_name} className="max-w-full max-h-[85vh] rounded-2xl object-contain" />
          <button onClick={() => setShowPhotoModal(false)} className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white dark:bg-dark-surface border shadow-lg flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      </motion.div>
    )}

    {/* Call modal */}
    <AlertDialog open={showCallModal} onOpenChange={setShowCallModal}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-lime-600" /> Call {student?.student_name}
          </AlertDialogTitle>
          <AlertDialogDescription>Choose a number to call</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 my-2">
          {[
            { label: 'Father', phone: student?.parents?.father_phone_1, sub: student?.parents?.father_phone_2, icon: '👨' },
            { label: 'Mother', phone: student?.parents?.mother_phone_1, sub: student?.parents?.mother_phone_2, icon: '👩' },
            { label: 'WhatsApp', phone: student?.parents?.whatsapp_number, icon: '💬' },
          ].map((item) => (
            (item.phone || item.sub) ? (
              <div key={item.label} className="border border-light-border dark:border-dark-border rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-light-text dark:text-dark-text">{item.icon} {item.label}</p>
                {item.phone && (
                  <a href={`tel:${item.phone}`} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-lime-50 dark:bg-lime-500/10 hover:bg-lime-100 dark:hover:bg-lime-500/20 transition-colors text-sm font-medium text-lime-700 dark:text-lime-300">
                    <Phone className="w-4 h-4" /> {item.phone}
                  </a>
                )}
                {item.sub && item.sub !== item.phone && (
                  <a href={`tel:${item.sub}`} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-lime-50 dark:bg-lime-500/10 hover:bg-lime-100 dark:hover:bg-lime-500/20 transition-colors text-sm font-medium text-lime-700 dark:text-lime-300">
                    <Phone className="w-4 h-4" /> {item.sub} <span className="text-[10px] text-lime-500 ml-auto">Alt</span>
                  </a>
                )}
              </div>
            ) : null
          ))}
          {!student?.parents?.father_phone_1 && !student?.parents?.mother_phone_1 && !student?.parents?.whatsapp_number && (
            <p className="text-center text-sm text-light-text-muted py-4">No phone numbers available</p>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Student</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to delete {student?.student_name}? All records will be permanently deleted.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => deleteStudent.mutate()}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function StudentAttendanceTab({ studentId }: { studentId: string }) {
  const { data: records, isLoading } = useQuery({
    queryKey: ['student-attendance', studentId],
    queryFn: async () => {
      const res = await api.get<any>(`/api/attendance/student/${studentId}`);
      return res.data || [];
    },
    enabled: !!studentId,
  });

  const stats = { present: 0, absent: 0, late: 0, leave: 0 };
  if (records && Array.isArray(records)) {
    records.forEach((r: any) => {
      const s = r.status?.toLowerCase();
      if (s in stats) stats[s as keyof typeof stats]++;
    });
  }

  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8" />;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="glass-card p-3 rounded-xl text-center">
          <p className="text-xl sm:text-2xl font-bold text-lime-500">{stats.present}</p>
          <p className="text-xs text-light-text-muted">Present</p>
        </div>
        <div className="glass-card p-3 rounded-xl text-center">
          <p className="text-xl sm:text-2xl font-bold text-rose-500">{stats.absent}</p>
          <p className="text-xs text-light-text-muted">Absent</p>
        </div>
        <div className="glass-card p-3 rounded-xl text-center">
          <p className="text-xl sm:text-2xl font-bold text-tangerine-500">{stats.late}</p>
          <p className="text-xs text-light-text-muted">Late</p>
        </div>
        <div className="glass-card p-3 rounded-xl text-center">
          <p className="text-xl sm:text-2xl font-bold text-electric-500">{stats.leave}</p>
          <p className="text-xs text-light-text-muted">Leave</p>
        </div>
      </div>

      {/* Calendar */}
      {records && Array.isArray(records) && records.length > 0 ? (
        <AttendanceCalendar records={records.map((r: any) => ({
          attendance_date: r.attendance_date || r.date,
          status: r.status?.toLowerCase() || 'present',
        }))} />
      ) : null}

      {records && Array.isArray(records) && records.length > 0 ? (
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="divide-y divide-light-border dark:divide-dark-border">
            {records.map((record: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 sm:p-4">
                <div>
                  <p className="text-sm font-medium text-light-text dark:text-dark-text">{formatDate(record.date || record.attendance_date)}</p>
                  <p className="text-xs text-light-text-muted">{record.class_name || record.className} - {record.section_name || record.sectionName}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  (record.status?.toLowerCase() || 'present') === 'present' ? 'bg-lime-100 text-lime-700 dark:bg-lime-500/20 dark:text-lime-300' :
                  (record.status?.toLowerCase() || 'present') === 'absent' ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300' :
                  (record.status?.toLowerCase() || 'present') === 'late' ? 'bg-tangerine-100 text-tangerine-700 dark:bg-tangerine-500/20 dark:text-tangerine-300' :
                  'bg-electric-100 text-electric-700 dark:bg-electric-500/20 dark:text-electric-300'
                }`}>
                  {record.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <CalendarDays className="w-10 h-10 sm:w-12 sm:h-12 text-light-text-muted mx-auto mb-3" />
          <p className="text-light-text-muted">No attendance records found</p>
        </div>
      )}
    </div>
  );
}

function StudentDocumentsTab({ studentId, documents }: { studentId: string; documents: Array<{ id: number; document_type: string; file_name: string; public_url?: string }> }) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const deleteDoc = useMutation({
    mutationFn: (docId: number) => api.delete(`/api/documents/${docId}`),
    onSuccess: () => {
      toast.success('Document deleted');
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
      queryClient.invalidateQueries({ queryKey: ['module-student-detail', studentId] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete'),
  });

  if (!documents.length) {
    return (
      <div className="text-center py-8">
        <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-light-text-muted mx-auto mb-3" />
        <p className="text-light-text-muted">No documents uploaded</p>
      </div>
    );
  }

  const isImage = (type: string) => type === 'student_photo' || type === 'image';

  return (
    <>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
      {documents.map((doc) => (
        <div key={doc.id} className="p-2 sm:p-3 rounded-xl bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border relative">
          <div className="flex items-start justify-between mb-1">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center overflow-hidden ${isImage(doc.document_type) ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-purple-100 dark:bg-purple-500/20'}`}>
              {isImage(doc.document_type) && doc.public_url ? (
                <img src={doc.public_url.startsWith('http') ? doc.public_url : `${API_URL}${doc.public_url}`} alt={doc.file_name} className="w-full h-full object-cover" />
              ) : isImage(doc.document_type) ? (
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-rose-500" />
              ) : (
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
              )}
            </div>
            <button
              onClick={() => setConfirmDelete(doc.id)}
              className="p-0.5 rounded hover:bg-rose-50 dark:hover:bg-rose-500/10"
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
            </button>
          </div>
          <p className="text-[10px] sm:text-xs font-medium text-light-text dark:text-dark-text truncate">{doc.file_name}</p>
          <p className="text-[9px] sm:text-[10px] text-light-text-muted capitalize">{doc.document_type.replace(/_/g, ' ')}</p>
        </div>
      ))}
    </div>
    <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Document</AlertDialogTitle>
          <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => {
            if (confirmDelete) deleteDoc.mutate(confirmDelete);
          }}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
